import React, { useEffect, useRef } from 'react';

const StargateTunnel = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);
    
    // Stars configuration
    const stars = [];
    const numStars = 400;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    // Initialize stars
    for (let i = 0; i < numStars; i++) {
      stars.push({
        x: Math.random() * canvas.width - centerX,
        y: Math.random() * canvas.height - centerY,
        z: Math.random() * 2000,
        size: Math.random() * 2 + 0.5,
        color: Math.random() > 0.7 ? '#60a5fa' : Math.random() > 0.5 ? '#93c5fd' : '#ffffff'
      });
    }
    
    // Tunnel rings
    const rings = [];
    const numRings = 15;
    for (let i = 0; i < numRings; i++) {
      rings.push({
        z: i * 150,
        rotation: Math.random() * Math.PI * 2
      });
    }
    
    // Animation loop
    const speed = 15;
    let time = 0;
    
    const animate = () => {
      time += 0.016;
      
      // Clear with fade effect for trails
      ctx.fillStyle = 'rgba(0, 5, 20, 0.15)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      
      // Draw tunnel rings
      rings.forEach((ring, i) => {
        ring.z -= speed * 0.5;
        ring.rotation += 0.02;
        
        if (ring.z < 0) {
          ring.z = 2000;
        }
        
        const perspective = 400 / (ring.z + 400);
        const radius = 800 * perspective;
        const alpha = Math.max(0, 1 - ring.z / 2000) * 0.3;
        
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(59, 130, 246, ${alpha})`;
        ctx.lineWidth = 2 * perspective;
        ctx.stroke();
        
        // Inner glow ring
        ctx.beginPath();
        ctx.arc(cx, cy, radius * 0.95, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(147, 197, 253, ${alpha * 0.5})`;
        ctx.lineWidth = 4 * perspective;
        ctx.stroke();
      });
      
      // Draw and update stars
      stars.forEach(star => {
        // Move star towards viewer
        star.z -= speed;
        
        // Reset star if it passes the viewer
        if (star.z <= 0) {
          star.z = 2000;
          star.x = Math.random() * canvas.width - cx;
          star.y = Math.random() * canvas.height - cy;
        }
        
        // Calculate 3D to 2D projection
        const perspective = 400 / star.z;
        const screenX = cx + star.x * perspective;
        const screenY = cy + star.y * perspective;
        
        // Calculate star size based on distance
        const size = star.size * perspective * 3;
        
        // Calculate brightness based on distance
        const brightness = Math.min(1, (2000 - star.z) / 1500);
        
        // Draw star trail
        const trailLength = Math.min(50, speed * perspective * 3);
        const trailX = cx + star.x * (400 / (star.z + speed * 2));
        const trailY = cy + star.y * (400 / (star.z + speed * 2));
        
        // Create gradient for trail
        const gradient = ctx.createLinearGradient(trailX, trailY, screenX, screenY);
        gradient.addColorStop(0, 'transparent');
        gradient.addColorStop(1, star.color);
        
        ctx.beginPath();
        ctx.moveTo(trailX, trailY);
        ctx.lineTo(screenX, screenY);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = size * 0.5;
        ctx.stroke();
        
        // Draw star
        ctx.beginPath();
        ctx.arc(screenX, screenY, size, 0, Math.PI * 2);
        ctx.fillStyle = star.color;
        ctx.globalAlpha = brightness;
        ctx.fill();
        ctx.globalAlpha = 1;
        
        // Add glow effect for closer stars
        if (star.z < 500) {
          ctx.beginPath();
          ctx.arc(screenX, screenY, size * 2, 0, Math.PI * 2);
          const glowGradient = ctx.createRadialGradient(screenX, screenY, 0, screenX, screenY, size * 2);
          glowGradient.addColorStop(0, star.color);
          glowGradient.addColorStop(1, 'transparent');
          ctx.fillStyle = glowGradient;
          ctx.fill();
        }
      });
      
      // Draw central vortex glow
      const vortexGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, 300);
      vortexGradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
      vortexGradient.addColorStop(0.1, 'rgba(147, 197, 253, 0.6)');
      vortexGradient.addColorStop(0.3, 'rgba(59, 130, 246, 0.3)');
      vortexGradient.addColorStop(0.6, 'rgba(37, 99, 235, 0.1)');
      vortexGradient.addColorStop(1, 'transparent');
      
      ctx.beginPath();
      ctx.arc(cx, cy, 300, 0, Math.PI * 2);
      ctx.fillStyle = vortexGradient;
      ctx.fill();
      
      // Pulsing center
      const pulseSize = 50 + Math.sin(time * 3) * 20;
      const centerGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, pulseSize);
      centerGradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
      centerGradient.addColorStop(0.5, 'rgba(147, 197, 253, 0.8)');
      centerGradient.addColorStop(1, 'transparent');
      
      ctx.beginPath();
      ctx.arc(cx, cy, pulseSize, 0, Math.PI * 2);
      ctx.fillStyle = centerGradient;
      ctx.fill();
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full"
      style={{ background: 'radial-gradient(ellipse at center, #0a1628 0%, #000510 100%)' }}
    />
  );
};

export default StargateTunnel;
